import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import orthopaedicsImg from '../assets/orthopaedics.webp';
import weightManagementImg from '../assets/weight-management.webp';
import dermatologyImg from '../assets/dermatology.webp';
import paediatricsImg from '../assets/paediatrics.webp';
import entCareImg from '../assets/ent-care.webp';
import mentalHealthImg from '../assets/mental-health.webp';
import endocrinologyImg from '../assets/endocrinology.webp';
import cardiologyImg from '../assets/cardiology.webp';
import ivfFertilityImg from '../assets/ivf-fertility.webp';
import specialistsImg from '../assets/specialists.webp';
import eyeCareImg from '../assets/eye-care.webp';
import dentalCareImg from '../assets/dental-care.webp';
import preventiveHealthImg from '../assets/preventive-health.webp';
import urologyImg from '../assets/urology.webp';
import heroFallback1Img from '../assets/hero-fallback-1.webp';
import heroFallback2Img from '../assets/hero-fallback-2.webp';
import heroFallback3Img from '../assets/hero-fallback-3.webp';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const images = [
    heroFallback1Img,
    heroFallback2Img,
    heroFallback3Img,
  ];

  const slides = [
    {
      title: 'Connect with Verified Specialists Today.',
      description: 'Access expert guidance without waiting or disruption.',
      cta: 'Start Consultation',
      image: specialistsImg,
    },
    {
      title: 'IVF & Fertility',
      description: 'Personalised fertility guidance and IVF consultation designed around your reproductive goals. Compassionate support at every stage of your fertility journey.',
      cta: 'Start Your Fertility Plan',
      image: ivfFertilityImg,
    },
    {
      title: 'Protect your heart. Act early.',
      description: 'Get specialist-led cardiac guidance without delays.',
      cta: 'Book Cardiology Consult',
      href: '/cardiology-services',
      image: cardiologyImg,
    },
    {
      title: 'Wellness & Preventive Health',
      description: 'Proactive screenings, lifestyle guidance, and preventive care plans. Stay ahead of illness with structured health management.',
      cta: 'Explore Preventive Care',
      image: preventiveHealthImg,
    },
    {
      title: 'Orthopaedics',
      description: 'Specialist care for joint pain, sports injuries, fractures, and surgical planning. Restore movement with structured orthopaedic evaluation.',
      cta: 'Book Orthopaedic Review',
      href: '/orthopaedic-services',
      image: orthopaedicsImg,
    },
    {
      title: 'Dermatology',
      description: 'Treat the root. Not just the skin. Get expert guidance for skin, hair, and nail conditions.',
      cta: 'Book Dermatology Consult',
      image: dermatologyImg,
      href: '/dermatology-service',
    },
    {
      title: 'Obesity / Weight Management',
      description: 'Achieve Sustainable Weight Goals. Structured guidance for safe and effective weight management.',
      bullets: ['BMI & metabolic assessment', 'Diet & lifestyle planning', 'Hormonal weight evaluation', 'Bariatric consultation', 'Long-term wellness planning'],
      cta: 'Start Weight Plan',
      image: weightManagementImg,
    },
    {
      title: 'Gentle Care for Growing Lives',
      description: 'Comprehensive child-focused medical support.',
      bullets: ['Child growth monitoring', 'Vaccination guidance', 'Fever & infection management', 'Nutritional support', 'Developmental assessment'],
      cta: 'Consult Pediatric Specialist',
      image: paediatricsImg,
    },
    {
      title: 'Clear Vision. Complete Eye Care.',
      description: 'Expert evaluation for vision and eye health.',
      bullets: ['Vision testing', 'Cataract consultation', 'Glaucoma screening', 'Dry eye management', 'Diabetic eye care'],
      cta: 'Book Eye Consultation',
      image: eyeCareImg,
    },
    {
      title: 'ENT Care',
      description: 'Specialist evaluation for sinus issues, hearing concerns, throat infections, and more. Targeted care for everyday ENT concerns.',
      cta: 'Consult ENT Specialist',
      image: entCareImg,
    },
    {
      title: 'Dental Care',
      description: 'Comprehensive dental consultations for oral health, cosmetic care, and restorative solutions. Protect your smile with expert dental guidance.',
      cta: 'Book Dental Appointment',
      image: dentalCareImg,
      href: '/dental-service',
    },
    {
      title: 'Endocrinology',
      description: 'Balance Your Hormones. Restore Control. Expert endocrine care with long-term monitoring.',
      cta: 'Speak to the Specialist',
      image: endocrinologyImg,
      href: '/virtual-endocrinology',
    },
    {
      title: 'Prioritise Your Mental Wellbeing',
      description: 'Compassionate mental health support tailored to you.',
      bullets: ['Anxiety & depression care', 'Stress management', 'Therapy guidance', 'Medication review', 'Adolescent mental health'],
      cta: 'Book Mental Health Consultation',
      image: mentalHealthImg,
    },
    {
      title: 'Advanced Kidney Care, Simplified',
      description: 'Expert guidance for acute and chronic kidney concerns.',
      cta: 'Connect with Nephrologist',
      href: '/nephrology-services',
    },
    {
      title: 'Urology',
      description: 'Expert urological care for kidney stones, bladder concerns, prostate health, and urinary tract conditions. Confidential consultations with experienced specialists.',
      cta: 'Book Urology Consultation',
      image: urologyImg,
    },
  ].map((slide, idx) => ({ ...slide, image: slide.image ?? images[idx % images.length] }));

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section className="relative min-h-screen lg:h-screen flex flex-col lg:flex-row overflow-hidden">
      {/* Left Side - Text Content (Changes with carousel) */}
      <div className="w-full lg:w-1/2 bg-gradient-to-br from-[#1e3a8a] via-[#2563eb] to-[#1e40af] flex items-center justify-center relative overflow-hidden">
        {/* Modern geometric background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        {/* Animated gradient orbs */}
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#22d3ee] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Diagonal accent line */}
        <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-transparent via-[#22d3ee] to-transparent"></div>

        <div className="relative z-10 w-full max-w-xl px-6 sm:px-8 lg:px-12 pt-24 sm:pt-28 pb-12 lg:py-0 flex flex-col justify-center">
          {/* Animated text content that changes with slides */}
          <div key={currentSlide} className="animate-fadeIn space-y-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white">
              {slides[currentSlide].title}
            </h1>

            <p className="text-white/90 text-sm sm:text-base md:text-lg leading-relaxed">
              {slides[currentSlide].description}
            </p>

            {slides[currentSlide].bullets && (
              <ul className="space-y-1.5">
                {slides[currentSlide].bullets.map((point, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/90 text-xs sm:text-sm">
                    <span className="w-1.5 h-1.5 bg-[#22d3ee] rounded-full flex-shrink-0"></span>
                    {point}
                  </li>
                ))}
              </ul>
            )}

            {slides[currentSlide].href ? (
              <Link to={slides[currentSlide].href}>
                <Button className="group bg-white text-[#1e3a8a] hover:bg-[#22d3ee] hover:text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full shadow-2xl hover:shadow-[#22d3ee]/50 transition-all duration-300 hover:scale-105 hover:shadow-xl w-fit">
                  <span className="flex items-center gap-2">
                    {slides[currentSlide].cta}
                    <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                  </span>
                </Button>
              </Link>
            ) : (
              <Button className="group bg-white text-[#1e3a8a] hover:bg-[#22d3ee] hover:text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full shadow-2xl hover:shadow-[#22d3ee]/50 transition-all duration-300 hover:scale-105 hover:shadow-xl w-fit">
                <span className="flex items-center gap-2">
                  {slides[currentSlide].cta}
                  <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                </span>
              </Button>
            )}

            {/* Slide indicators — in flow, below button */}
            <div className="flex items-center space-x-2 pt-2 overflow-hidden max-w-full">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  aria-label={`Go to slide ${idx + 1}`}
                  onClick={() => setCurrentSlide(idx)}
                  className={`group relative transition-all duration-300 flex-shrink-0 py-2 ${
                    idx === currentSlide ? 'w-10 sm:w-12' : 'w-6 sm:w-8'
                  }`}
                >
                  <div className={`h-1.5 rounded-full transition-all duration-300 ${
                    idx === currentSlide
                      ? 'bg-[#22d3ee] shadow-lg shadow-[#22d3ee]/50'
                      : 'bg-white/30 group-hover:bg-white/50'
                  }`}></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Creative Carousel Layout */}
      <div className="w-full lg:w-1/2 relative bg-gradient-to-br from-gray-50 to-gray-100 min-h-[480px] sm:min-h-[560px] lg:min-h-0">
        {/* Clean split with subtle gradient border */}
        <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-transparent via-[#22d3ee] to-transparent"></div>

        {/* Creative overlapping card layout - Better centered */}
        <div className="relative w-full h-full min-h-[480px] sm:min-h-[560px] lg:min-h-0 overflow-hidden flex items-center justify-center">
          {/* Background decorative elements */}
          <div className="absolute top-10 right-10 w-40 h-40 bg-[#22d3ee]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-60 h-60 bg-[#1e3a8a]/10 rounded-full blur-3xl"></div>

          {/* Main carousel cards - only render active + neighbours */}
          <div className="relative w-full h-full flex items-center justify-center">
            {[
              { idx: (currentSlide - 1 + slides.length) % slides.length, role: 'prev' },
              { idx: currentSlide, role: 'active' },
              { idx: (currentSlide + 1) % slides.length, role: 'next' },
            ].map(({ idx, role }) => {
              const slide = slides[idx];
              const isActive = role === 'active';
              const isPrev = role === 'prev';
              const isNext = role === 'next';

              const zIndex = isActive ? 30 : 20;
              const transform = isActive
                ? 'translate(-50%, -50%) rotate(0deg) scale(1)'
                : isPrev
                ? 'translate(calc(-50% - 150px), calc(-50% + 40px)) rotate(-8deg) scale(0.88)'
                : 'translate(calc(-50% + 150px), calc(-50% + 40px)) rotate(8deg) scale(0.88)';
              const opacity = isActive ? 1 : 0.5;

              return (
                <div
                  key={role}
                  className="absolute top-1/2 left-1/2 transition-all duration-700 ease-out cursor-pointer"
                  style={{ zIndex, transform, opacity }}
                  onClick={() => setCurrentSlide(idx)}
                >
                  <div className="w-[300px] h-[400px] sm:w-[400px] sm:h-[500px] lg:w-[550px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300 border-4 border-white bg-white">
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                      loading={isActive ? 'eager' : 'lazy'}
                      fetchpriority={isActive && currentSlide === 0 ? 'high' : 'auto'}
                      decoding="async"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>

                    {/* Decorative corner accents */}
                    <div className="absolute top-5 right-5 w-14 h-14 border-2 border-white/60 rounded-full backdrop-blur-sm"></div>
                    <div className="absolute bottom-5 left-5 w-20 h-20 border-2 border-white/60 rounded-tl-3xl backdrop-blur-sm"></div>

                    {/* Card info badge */}
                    {isActive && (
                      <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-xl rounded-2xl p-4 shadow-xl animate-fadeIn">
                        <p className="text-lg font-bold text-[#1e3a8a] mb-1">{slide.title}</p>
                        <p className="text-sm text-gray-600">{slide.cta}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Modern Navigation Arrows - Better positioned */}
          <button
            aria-label="Previous slide"
            onClick={prevSlide}
            className="absolute left-4 sm:left-8 lg:left-12 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white/90 backdrop-blur-xl rounded-2xl flex items-center justify-center shadow-xl hover:shadow-2xl hover:bg-[#22d3ee] hover:text-white transition-all duration-300 hover:scale-110 z-40 group"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-[#1e3a8a] group-hover:text-white transition-colors duration-300" />
          </button>
          <button
            aria-label="Next slide"
            onClick={nextSlide}
            className="absolute right-4 sm:right-8 lg:right-12 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white/90 backdrop-blur-xl rounded-2xl flex items-center justify-center shadow-xl hover:shadow-2xl hover:bg-[#22d3ee] hover:text-white transition-all duration-300 hover:scale-110 z-40 group"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-[#1e3a8a] group-hover:text-white transition-colors duration-300" />
          </button>

          {/* Progress indicator dots - Bottom Center */}
          <div className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 flex space-x-2 sm:space-x-3 z-40 overflow-hidden max-w-[90%]">
            {slides.map((_, idx) => (
              <button
                key={idx}
                aria-label={`Go to slide ${idx + 1}`}
                onClick={() => setCurrentSlide(idx)}
                className="group relative flex-shrink-0 py-3 px-1 min-w-[24px]"
              >
                <div className={`h-2 rounded-full transition-all duration-300 ${
                  idx === currentSlide
                    ? 'bg-[#22d3ee] w-10 sm:w-12 shadow-lg shadow-[#22d3ee]/50'
                    : 'bg-gray-400 w-2 group-hover:bg-gray-600'
                }`}></div>
              </button>
            ))}
          </div>
        </div>
      </div>

    </section>
  );
};

export default HeroSection;